import { useState } from 'react';
import axios from 'axios';
import { X, Sparkles, Loader2 } from 'lucide-react';
import UltimateTag from './UltimateTag';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal for Ultimate subscribers to set a custom 1-4 word display tag.
 *
 * Props:
 *  - currentTag: string | null
 *  - onClose(): close modal
 *  - onSaved(newTag): callback fired after a successful save (newTag may be null if cleared)
 */
export default function UltimateTagModal({ currentTag, onClose, onSaved }) {
  const [value, setValue] = useState(currentTag || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
  const valid = value.trim().length === 0 || (wordCount >= 1 && wordCount <= 4 && value.trim().length <= 24);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/user/ultimate-tag`,
        { tag: value.trim() },
        { withCredentials: true }
      );
      onSaved(data.ultimate_tag);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save tag.');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3"
      onClick={onClose}
      data-testid="ultimate-tag-modal"
    >
      <div
        className="w-full max-w-md rounded-3xl border border-pink-400/30 bg-gradient-to-br from-[#1B0E2E] via-[#250C20] to-[#1A0A1A] p-5 shadow-[0_0_40px_rgba(244,114,182,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="flex items-center gap-2 font-black text-white text-base font-['Unbounded']">
            <Sparkles className="w-4 h-4 text-pink-300" />
            Your Ultimate Tag
          </h3>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="tag-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-white/60 mb-4">
          A custom label that appears next to your name everywhere. 1 to 4 words, max 24 characters. Changeable once every 7 days.
        </p>

        {/* Live preview */}
        <div className="mb-3 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 flex items-center gap-2 flex-wrap min-h-[40px]">
          <span className="text-sm text-white font-medium">Preview:</span>
          <span className="text-sm text-white">You</span>
          {value.trim() && <UltimateTag tag={value.trim()} />}
        </div>

        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. Founder Vibes"
          maxLength={30}
          className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 focus:border-pink-400/60 focus:outline-none text-white text-sm"
          data-testid="tag-input"
          autoFocus
        />

        <div className="mt-2 flex items-center justify-between text-[11px] text-white/40">
          <span>{wordCount}/4 words · {value.trim().length}/24 chars</span>
          {!valid && <span className="text-amber-400">Must be 1-4 words, ≤24 chars</span>}
        </div>

        {error && <p className="mt-2 text-xs text-rose-400" data-testid="tag-error">{error}</p>}

        <div className="mt-4 flex gap-2">
          {currentTag && (
            <button
              onClick={() => { setValue(''); }}
              className="flex-1 py-2.5 rounded-full text-sm font-bold bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
              data-testid="tag-clear-btn"
            >
              Clear
            </button>
          )}
          <button
            onClick={save}
            disabled={saving || !valid}
            className="flex-1 py-2.5 rounded-full text-sm font-black text-[#2A0314] bg-gradient-to-r from-[#F472B6] via-[#F59E0B] to-[#FCD34D] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
            data-testid="tag-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Tag
          </button>
        </div>
      </div>
    </div>
  );
}
